import logo from "@/assets/logos/SnapShipLogoNEW.svg";
import { createUserWithEmailAndPassword, deleteUser, updateProfile } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import Form from "@/components/composite/Form";
import { auth, db } from "@/core/firebase";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "@/components/composite/Inputs/Input";
import { signUpSchema } from "@/schema/UserSchema";
import LoadingButton from "@/components/composite/Buttons/LoadingButton";
import { useState } from "react";
import { UserData } from "@shared/UserTypes";
import { useUserStore } from "@/core/UserStore";
import Trucks from "@/assets/misc/Trucks.svg";
import { createStripeCustomer } from "@/api/user/editStripeCustomer";
import { sendWelcomeEmail } from "@/api/emails/sendWelcomeEmail";
import { useAppContext } from "@/onboarding/context";

// create sign-in schema

export default function SignUpForm() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [showPartnershipCodeInput, setShowPartnershipCodeInput] = useState(false);
    const {
        setState,
        state: { run, tourActive }
    } = useAppContext();

    const methods = useForm<z.infer<typeof signUpSchema>>({
        resolver: zodResolver(signUpSchema),
        mode: "all",
        reValidateMode: "onChange"
    });

    const onSubmit = (data: z.infer<typeof signUpSchema>) => {
        setIsLoading(true);
        if (data.email.trim().toLowerCase().endsWith("@protonmail.com")) {
            methods.setError("email", { message: "This email is not allowed" });
            setIsLoading(false);
            return;
        }
        createUserWithEmailAndPassword(auth, data.email, data.password)
            .then(async (userCredential) => {
                const user = userCredential.user; // created the user

                if (user !== null && user.email !== null) {
                    // if it fails after this point, it should delete the user from auth
                    updateProfile(user, { displayName: data.name }); // adds name to the user object in firebase auth

                    const stripeID = await createStripeCustomer(user.email, data.name, data.companyName);

                    let userData: UserData = {
                        name: data.name,
                        companyName: data.companyName,
                        email: user.email,
                        phoneNumber: data.phoneNumber,
                        stripe: stripeID,
                        activeCarriers: {
                            UPS: {
                                enabled: false,
                                dateSigned: null
                            },
                            FedEx: {
                                enabled: false,
                                dateSigned: null
                            },
                            Purolator: {
                                enabled: false,
                                dateSigned: null
                            },
                            GLS: {
                                enabled: false,
                                dateSigned: null
                            }
                        }
                    };

                    if (data.partnershipCode) {
                        userData = { ...userData, partnerships: data.partnershipCode };
                    }

                    if (user.email === null) {
                        // if the user email doesn't exist it should throw so that we don't get null in database
                        throw user;
                    }
                    await setDoc(doc(db, "users", user.email), userData);
                    await sendWelcomeEmail(user.email);
                    useUserStore.getState().setUser(user);
                    useUserStore.getState().setUserData(userData);
                    navigate("/settings/carriers", { replace: true });
                    setState({ run: true, tourActive: true, stepIndex: 0 });
                } else {
                    await deleteUser(user);
                    throw new Error("User creation failed");
                }
            })
            .catch((error) => {
                const errorCode = error.code;
                const errorMessage = error.message;
                console.log(errorCode, errorMessage);
                let message: string = "";
                if (errorCode === "auth/email-already-in-use") {
                    message = "Email already in use";
                    methods.setError("email", {
                        message: message
                    });
                } else if (typeof errorMessage === "string" && errorMessage.includes("New signups are currently paused")) {
                    message = "New signups are currently paused. Please try again later.";
                    methods.setError("root", {
                        message: message
                    });
                } else if (
                    typeof errorMessage === "string" &&
                    (errorMessage.includes("BLOCKING_FUNCTION_ERROR_RESPONSE") ||
                        errorMessage.includes("PERMISSION_DENIED") ||
                        errorMessage.includes("This account is not allowed"))
                ) {
                    message = "This account is not allowed";
                    methods.setError("email", {
                        message: message
                    });
                } else {
                    message = `${errorCode} ${errorMessage}`;
                    methods.setError("root", {
                        message: message
                    });
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const displayPartnershipCodeInput = () => {
        setShowPartnershipCodeInput(true);
    };

    // TODO: google sign up
    // const handleGoogleSignUp = (e) => {
    //     const provider = new GoogleAuthProvider();
    //     const auth = getAuth();
    //     provider.addScope("https://www.googleapis.com/auth/userinfo.email");
    //     provider.addScope("https://www.googleapis.com/auth/userinfo.profile");
    //     auth.useDeviceLanguage();
    //     signInWithRedirect(auth, provider);
    // };

    // getRedirectResult(auth)
    //     .then(async (result) => {
    //         console.log("Starting google auth..");
    //         console.log("Result:", result);
    //         if (result) {
    //             const credential = GoogleAuthProvider.credentialFromResult(result);
    //             const token = credential?.accessToken;

    //             const user = result.user;
    //             let stripeID;
    //             if (user.email !== null) {
    //                 stripeID = await createStripeCustomer(user.email);
    //             }
    //             const userData: UserData = {
    //                 // user: user,
    //                 name: user.displayName ?? "N/A",
    //                 companyName: "N/A",
    //                 email: user.email ?? "N/A",
    //                 phoneNumber: user.phoneNumber ?? "N/A",
    //                 stripe: stripeID,
    //                 activeCarriers: {
    //                     UPS: {
    //                         enabled: false,
    //                         dateSigned: null
    //                     },
    //                     FedEx: {
    //                         enabled: false,
    //                         dateSigned: null
    //                     },
    //                     Purolator: {
    //                         enabled: false,
    //                         dateSigned: null
    //                     }
    //                 }
    //             };
    //             if (user.email !== null) {
    //                 await setDoc(doc(db, "users", user.email), userData);
    //                 useUserStore.getState().setUser(user);
    //                 useUserStore.getState().setUserData(userData);
    //             } else {
    //                 throw new Error("User email is null");
    //             }
    //             navigate("/settings/carrier", { replace: true });

    //             setIsOpen(true);
    //             setCurrentStep(0);
    //         }
    //     })
    //     .catch((error) => {
    //         // Handle Errors here.
    //         const errorCode = error.code;
    //         const errorMessage = error.message;
    //         // The email of the user's account used.
    //         const email = error.customData.email;
    //         // The AuthCredential type that was used.
    //         const credential = GoogleAuthProvider.credentialFromError(error);
    //         console.error(errorCode, errorMessage);
    //         // ...
    //     });

    return (
        <div className="w-full lg:grid  lg:grid-cols-2 ">
            <div className="flex flex-col items-center bg-white">
                <div className="p-9 md:place-self-start">
                    <a href="https://www.snapship.co">
                        <img src={logo} className="h-10" />
                    </a>
                </div>
                <div className="m-auto grid max-h-[80vh] w-[350px] gap-6 overflow-y-auto px-2">
                    <div className="grid gap-2 text-center">
                        <h1 className="text-3xl font-bold">Sign Up</h1>
                        <p className="text-muted-foreground text-balance">Fill in the form below to sign up for free</p>
                    </div>
                    <Form methods={methods} onSubmit={onSubmit} className="grid gap-4">
                        <div className="grid grid-cols-2 gap-4">
                            <Input label="Name" name="name" type="text" placeholder="John O'Brien" />
                            <Input label="Company name" name="companyName" type="text" placeholder="Scrub Co" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Input label="Email" name="email" type="email" placeholder="m@example.com" />
                            <Input label="Phone number" name="phoneNumber" type="text" placeholder="416-555-5555" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Input label="Password" name="password" type="password" placeholder="Enter password" />
                            <Input label="Confirm password" name="confirmPassword" type="password" placeholder="Re-enter password" />
                        </div>
                        {showPartnershipCodeInput && <Input label="Partnership code (optional)" name="partnershipCode" placeholder="Enter partnership code" />}
                        {!showPartnershipCodeInput && (
                            <div className="text-muted-foreground text-sm">
                                Have a partnership code? Click{" "}
                                <span onClick={displayPartnershipCodeInput} className="cursor-pointer text-blue-400 underline">
                                    here
                                </span>{" "}
                                to use it.
                            </div>
                        )}
                        <div className="text-muted-foreground text-sm">
                            By clicking Sign Up, you agree to SnapShip's{" "}
                            <a target="_blank" rel="noopener noreferrer" href="https://snapship.co/terms-of-use/" className="text-blue-400 underline">
                                Terms of Use
                            </a>{" "}
                            and{" "}
                            <a href="https://snapship.co/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">
                                Privacy Policy
                            </a>
                            .
                        </div>
                        <LoadingButton isLoading={isLoading} className="w-full" type="submit">
                            Sign up
                        </LoadingButton>
                        {/* <Button
                            variant="outline"
                            className="w-full"
                            onClick={(e) => {
                                e.preventDefault();
                                handleGoogleSignUp(e);
                            }}>
                            <img src={GoogleIcon} alt="Google Logo" className="mr-2 h-5 w-5" />
                            Sign up with Google
                        </Button> */}
                    </Form>
                    <div className="mt-4 text-center text-sm">
                        Already have an account?{" "}
                        <Link to="/login" className="underline">
                            Log in
                        </Link>
                    </div>
                </div>
            </div>
            <div className="hidden lg:block" style={{ backgroundColor: "#FCFCFC" }}>
                <img src={Trucks} alt="" className="max-h-screen w-full" />
            </div>
        </div>
    );
}
